import React, { useEffect, useState, useMemo } from "react";
import HeaderPrint from "../../../Pdf/PrintHeaderAccount";
import axios from "axios";
import config from "../../../config";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import PrintFooter from "../../../Pdf/PrintFooter";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import { Spin } from "antd";
import moment from "moment";

const COL = {
  leftLabel: 170,
  leftAmount: 113,
  rightLabel: 170,
  rightAmount: 114,
};

const TABLE_WIDTH =
  COL.leftLabel + COL.leftAmount + COL.rightLabel + COL.rightAmount;

const ASSET_ROWS = [
  { label: "Customer receivable", key: "customerReceivable" },
  { label: "Supplier receivable", key: "supplierReceivable" },
  { label: "Other receivables", key: "otherReceivable" },
  { label: "Inventory (at cost)", key: "totalStockValue" },
  { label: "Cash in hand", key: "totalCashInHand" },
  { label: "Cash at bank", key: "totalCashAtBank" },
];

const LIABILITY_ROWS = [
  { label: "Customer payable", key: "customerPayable" },
  { label: "Supplier payable", key: "supplierPayable" },
  { label: "Other payables", key: "otherPayable" },
];

const fmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);

function buildSideBySideRows(data) {
  const leftRows = ASSET_ROWS.map((row) => ({
    label: row.label,
    amount: data[row.key],
  }));
  const rightRows = LIABILITY_ROWS.map((row) => ({
    label: row.label,
    amount: data[row.key],
  }));
  const count = Math.max(leftRows.length, rightRows.length);
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push({ left: leftRows[i], right: rightRows[i] });
  }
  return rows;
}

const PrintWorkingCapital = () => {
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const urlParams = new URLSearchParams(window.location.search);
  const AsOfDate =
    urlParams.get("AsOfDate") ||
    urlParams.get("date") ||
    moment().format("YYYY-MM-DD");

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const formattedAsOf = moment(AsOfDate).isValid()
    ? moment(AsOfDate).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");

  useEffect(() => {
    document.title = "Print Working Capital";
    if (!UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/GetWorkingCapitalReport",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        Dated: formattedAsOf,
      },
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setData(response.data);
        } else {
          setData({});
        }
        setLoading(false);
      })
      .catch(() => {
        setData({});
        setLoading(false);
      });
  }, [formattedAsOf, UserID, AccessKey]);

  const asOfLabel =
    data.asOfDate && moment(data.asOfDate).isValid()
      ? moment(data.asOfDate).format(config.date_format)
      : moment(formattedAsOf).format(config.date_format);

  const tableRows = useMemo(() => buildSideBySideRows(data), [data]);

  const totals = useMemo(() => {
    const totalCurrentAssets =
      parseReportAmount(data.totalCurrentAssets) ||
      ASSET_ROWS.reduce((sum, row) => sum + parseReportAmount(data[row.key]), 0);
    const totalCurrentLiabilities =
      parseReportAmount(data.totalCurrentLiabilities) ||
      LIABILITY_ROWS.reduce(
        (sum, row) => sum + parseReportAmount(data[row.key]),
        0
      );
    const workingCapital =
      parseReportAmount(data.workingCapital) ||
      totalCurrentAssets - totalCurrentLiabilities;
    const currentRatio =
      parseReportAmount(data.currentRatio) ||
      (totalCurrentLiabilities > 0
        ? totalCurrentAssets / totalCurrentLiabilities
        : 0);
    return { totalCurrentAssets, totalCurrentLiabilities, workingCapital, currentRatio };
  }, [data]);

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <HeaderPrint compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Working Capital Report</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              As of: {asOfLabel}
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Current assets − current liabilities (short-term liquidity)
            </Text>
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={[saleReportStyles.tableHead, { width: TABLE_WIDTH }]}>
              <View style={[saleReportStyles.th, { width: COL.leftLabel }]}>
                <Text style={saleReportStyles.thValue}>Current Assets</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.leftAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.rightLabel }]}>
                <Text style={saleReportStyles.thValue}>Current Liabilities</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
            </View>

            {tableRows.map((row, index) => (
              <View key={index} style={saleReportStyles.row}>
                <View style={[saleReportStyles.td, { width: COL.leftLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.left?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.leftAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.left ? fmt(row.left.amount) : ""}
                  </Text>
                </View>
                <View style={[saleReportStyles.td, { width: COL.rightLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.right?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.rightAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.right ? fmt(row.right.amount) : ""}
                  </Text>
                </View>
              </View>
            ))}

            <View style={saleReportStyles.totalsRow}>
              <View style={[saleReportStyles.th, { width: COL.leftLabel }]}>
                <Text style={saleReportStyles.thValue}>Total current assets</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.leftAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.totalCurrentAssets)}
                </Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.rightLabel }]}>
                <Text style={saleReportStyles.thValue}>
                  Total current liabilities
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.totalCurrentLiabilities)}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={[
              saleReportStyles.table,
              { width: TABLE_WIDTH, marginTop: 8 },
            ]}
          >
            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: COL.leftLabel + COL.leftAmount + COL.rightLabel,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Working capital</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.workingCapital)}
                </Text>
              </View>
            </View>
            <View style={saleReportStyles.row}>
              <View
                style={[
                  saleReportStyles.td,
                  {
                    width: COL.leftLabel + COL.leftAmount + COL.rightLabel,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.tdValue}>
                  Current ratio (assets ÷ liabilities)
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.td,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.tdValue}>
                  {totals.totalCurrentLiabilities > 0
                    ? totals.currentRatio.toFixed(2)
                    : "—"}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={{ marginBottom: 20, marginTop: 0, top: 6, bottom: 6 }}
            fixed
          >
            <Text
              style={{ fontSize: "7pt", color: "#000" }}
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%" fileName="WorkingCapital.pdf">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintWorkingCapital;
